'use client'

import { cn } from '@/_lib/utils'
import { AOrderMobileCartSummary } from '@cart/_components/a-cart-mobile-summary'


import {
	ORDER_FLOW_STEPS,
	type OrderFlowStep,
	type OrderFlowStepDefinition
} from '../../../_constants/order-flow-steps.const'
import {
	canReachOrderFlowStep,
	type OrderFlowStepReachabilityContext
} from '../../../_utils/order-flow-step-guards'

import {
	AOrderProgressBrowserBackButton,
	AOrderProgressStepNavBackButton,
	AOrderProgressStepNavForwardButton,
	ORDER_PROGRESS_ARROW_OUTER_CLASS
} from './a-order-progress-arrow-buttons'

type AOrderProgressMobileProps = {
	stepDefs: OrderFlowStepDefinition[]
	stepCount: number
	isSingleStep: boolean
	activeDef: OrderFlowStepDefinition
	currentStep: OrderFlowStep
	maxStepReached: OrderFlowStep
	prevStep: OrderFlowStep
	nextStep: OrderFlowStep
	canGoBack: boolean
	canGoForward: boolean
	showBackArrow: boolean
	showForwardArrow: boolean
	goToStep: (step: OrderFlowStep) => void
	reachability: OrderFlowStepReachabilityContext
}

/** Mobile header slot: arrows, step title, cart snippet, and compact segment track. */
export function AOrderProgressMobile({
	stepDefs,
	stepCount,
	isSingleStep,
	activeDef,
	currentStep,
	maxStepReached,
	prevStep,
	nextStep,
	canGoBack,
	canGoForward,
	showBackArrow,
	showForwardArrow,
	goToStep,
	reachability
}: AOrderProgressMobileProps) {
	return (
		<div className='flex flex-col border-b border-medante-purple-dark/10 bg-medante-background lg:border-b-0'>
			<div className='flex flex-row items-center gap-2 px-4 py-2'>
				<div className={ORDER_PROGRESS_ARROW_OUTER_CLASS}>
					{isSingleStep ? (
						<AOrderProgressBrowserBackButton />
					) : showBackArrow ? (
						<AOrderProgressStepNavBackButton
							disabled={!canGoBack}
							onClick={() => goToStep(prevStep)}
						/>
					) : null}
				</div>

				<div
					key={currentStep}
					className='min-w-0 flex-1 animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300 motion-reduce:animate-none'
				>
					<p className='text-center text-sm font-medium'>
						{!isSingleStep && (
							<span className='font-bold'>0{stepDefs.indexOf(activeDef) + 1}. </span>
						)}
						{activeDef.label}
					</p>
				</div>

				<div className={ORDER_PROGRESS_ARROW_OUTER_CLASS}>
					{isSingleStep ? null : showForwardArrow ? (
						<AOrderProgressStepNavForwardButton
							disabled={!canGoForward}
							onClick={() => goToStep(nextStep)}
						/>
					) : null}
				</div>
			</div>
			<AOrderMobileCartSummary
				className='px-4 pt-2 pb-1'
				patientStepHeaderMinimal={currentStep === ORDER_FLOW_STEPS.PERSONAL_DETAILS}
			/>
			{stepCount > 1 && (
				<div className='flex gap-1 px-4 pb-2' role='group' aria-label='Postup objednávky'>
					{stepDefs.map(({ step, label }) => {
						const isClickable =
							step <= maxStepReached && canReachOrderFlowStep(step, reachability)
						const isVisitedFuture = step > currentStep && step <= maxStepReached
						const isPast = step < currentStep
						const isCurrent = step === currentStep
						const isLocked = step > maxStepReached

						return (
							<button
								key={step}
								type='button'
								className='group flex min-h-[3px] flex-1 flex-col justify-end p-0'
								disabled={!isClickable}
								onClick={() => goToStep(step)}
								aria-current={isCurrent ? 'step' : undefined}
								aria-label={label}
							>
								<div
									className={cn(
										'h-[3px] min-h-[3px] w-full rounded-full transition-colors duration-300 ease-out',
										isPast && 'bg-medante-pink-secondary',
										isCurrent && 'bg-medante-pink-secondary',
										isVisitedFuture && 'bg-medante-purple/50',
										isLocked && 'bg-gray-200',
										isVisitedFuture && 'group-hover:bg-medante-purple-dark'
									)}
								/>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}
